import { ArrowLeft, CheckCircle2, Circle, Loader2, Trash2, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Container } from '../../components/ui/Container';
import { PageHero } from '../../components/ui/PageHero';
import { formatDocTitle } from '../../lib/document-display';
import { pb } from '../../lib/pb';
import { useDialog } from '../../lib/useDialog';

interface AdminDocument {
  id: string;
  title: string;
  category: string;
  is_owner_only: boolean;
  is_admin_only: boolean;
  is_approved: boolean;
  status: string;
  original_filename: string;
  file: string;
  collectionId: string;
  created: string;
}

function buildFileUrl(doc: AdminDocument, fileToken: string): string {
  const opts = fileToken ? { download: true, token: fileToken } : { download: true };
  return pb.files.getURL(doc, doc.file, opts);
}

const CATEGORIES = [
  { value: 'legal', label: 'Compliance Reports' },
  { value: 'minutes', label: 'Meeting Minutes' },
  { value: 'financials', label: 'Financials' },
  { value: 'president-report', label: "President's Report" },
  { value: 'contract-bids', label: 'Contracts & Bids' },
  { value: 'emergency', label: 'Emergency Information' },
  { value: 'owner-info', label: 'Owner Information' },
  { value: 'declaration', label: 'Governing Documents' },
  { value: 'misc', label: 'Other' },
] as const;

const STATUS_BADGE: Record<string, { label: string; classes: string }> = {
  processing: { label: 'Processing', classes: 'bg-yellow-100 text-yellow-800' },
  ready: { label: 'Ready', classes: 'bg-green-100 text-green-800' },
  failed: { label: 'Failed', classes: 'bg-red-100 text-red-800' },
};

function categoryLabel(value: string): string {
  return CATEGORIES.find((c) => c.value === value)?.label ?? value;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function AdminDocuments(): JSX.Element {
  const [docs, setDocs] = useState<AdminDocument[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fileToken, setFileToken] = useState<string>('');
  const { confirm, alert, dialog } = useDialog();

  useEffect(() => {
    let cancelled = false;
    pb.files
      .getToken()
      .then((t) => {
        if (!cancelled) setFileToken(t);
      })
      .catch((err) => {
        console.error('file token fetch failed', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Upload form state
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<string>('minutes');
  const [isOwnerOnly, setIsOwnerOnly] = useState(false);
  const [isAdminOnly, setIsAdminOnly] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);

  useEffect(() => {
    const previous = document.title;
    document.title = 'Admin · Documents | Sunset Harbor Condominiums';
    return () => {
      document.title = previous;
    };
  }, []);

  async function loadDocs(): Promise<void> {
    setLoadError(null);
    try {
      const result = await pb.collection('documents').getList<AdminDocument>(1, 200, {
        sort: '-created',
      });
      setDocs(result.items);
    } catch (err) {
      console.error(err);
      setLoadError('Could not load documents. Refresh in a moment.');
    }
  }

  useEffect(() => {
    void loadDocs();
  }, []);

  function onFileSelected(e: React.ChangeEvent<HTMLInputElement>): void {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    if (f && !title) {
      // Pre-fill title with the filename stem (strip extension + clean up)
      const stem = f.name
        .replace(/\.[^.]+$/, '')
        .replace(/[-_]+/g, ' ')
        .trim();
      setTitle(stem);
    }
  }

  async function onUpload(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setUploadError(null);
    setUploadSuccess(null);
    if (!file) {
      setUploadError('Select a file to upload.');
      return;
    }
    if (!title.trim()) {
      setUploadError('Enter a title.');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('title', title.trim());
      formData.append('category', category);
      formData.append('is_owner_only', String(isOwnerOnly && !isAdminOnly));
      formData.append('is_admin_only', String(isAdminOnly));
      // Approval state only meaningful for minutes. For every other category
      // we force false so the flag stays clean if Kyle ever extends it.
      formData.append('is_approved', String(category === 'minutes' ? isApproved : false));
      // processed_pdf only accepts PDFs. For any other type, conversion.pb.js
      // converts the upload server-side and fills processed_pdf itself.
      const isPdf = file.name.toLowerCase().endsWith('.pdf');
      formData.append('status', isPdf ? 'ready' : 'processing');
      formData.append('original_filename', file.name);
      formData.append('file', file, file.name);
      if (isPdf) {
        formData.append('processed_pdf', file, file.name);
      }

      await pb.collection('documents').create(formData);
      setUploadSuccess(`Uploaded "${title.trim()}".`);
      setFile(null);
      setTitle('');
      setCategory('minutes');
      setIsOwnerOnly(false);
      setIsAdminOnly(false);
      setIsApproved(false);
      // Reset the file input
      const input = document.getElementById('admin-doc-file') as HTMLInputElement | null;
      if (input) input.value = '';
      await loadDocs();
    } catch (err) {
      console.error(err);
      setUploadError(
        err instanceof Error ? err.message : 'Upload failed. Check the file type and try again.',
      );
    } finally {
      setUploading(false);
    }
  }

  async function onToggleApproval(doc: AdminDocument): Promise<void> {
    const next = !doc.is_approved;
    const verb = next ? 'Approve' : 'Mark unapproved';
    const ok = await confirm(`${verb} "${doc.title}"? The change will be recorded in the audit log.`);
    if (!ok) return;
    setApprovingId(doc.id);
    try {
      await pb.collection('documents').update(doc.id, { is_approved: next });
      await loadDocs();
    } catch (err) {
      console.error(err);
      await alert('Update failed. Refresh and try again.');
    } finally {
      setApprovingId(null);
    }
  }

  async function onDelete(doc: AdminDocument): Promise<void> {
    const ok = await confirm(`Delete "${doc.title}"? This cannot be undone.`);
    if (!ok) return;
    try {
      await pb.collection('documents').delete(doc.id);
      await loadDocs();
    } catch (err) {
      console.error(err);
      await alert(
        'Delete failed. The document may already be gone or you may not have permission.',
      );
    }
  }

  return (
    <>
      {dialog}
      <PageHero
        eyebrow="Admin"
        title="Manage documents"
        lead="Upload new records, edit metadata, remove obsolete files. Public docs land on the Documents page; owner-only docs land on the Portal."
      />

      <section className="section bg-brand-white">
        <Container>
          <div className="mb-8">
            <Link
              to="/portal"
              className="inline-flex items-center gap-2 text-sm font-semibold text-brand-charcoal underline decoration-brand-yellow-deep/40 underline-offset-4 hover:decoration-brand-yellow-deep"
            >
              <ArrowLeft size={14} aria-hidden="true" />
              Back to portal
            </Link>
          </div>

          {/* Upload form */}
          <div className="mb-12 rounded-2xl border border-brand-surface bg-brand-white p-8 shadow-card md:p-10">
            <div className="mb-6 flex items-start gap-4">
              <span
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-yellow text-brand-charcoal"
                aria-hidden="true"
              >
                <Upload size={20} strokeWidth={1.75} />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-yellow-deep">
                  Upload
                </p>
                <h2 className="mt-1 text-xl font-bold text-brand-charcoal">Add a document</h2>
              </div>
            </div>

            <div className="mb-6 rounded-md border border-brand-yellow/40 bg-brand-yellow/10 px-4 py-3 text-sm text-brand-charcoal">
              <strong className="font-semibold">Before you upload:</strong> remove or redact owner
              signatures and personal contact details. Anything posted to the public or owner-only
              side can be downloaded and printed.
            </div>

            {uploadError ? (
              <div
                role="alert"
                className="mb-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
              >
                {uploadError}
              </div>
            ) : null}
            {uploadSuccess ? (
              <div
                role="status"
                className="mb-6 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800"
              >
                {uploadSuccess}
              </div>
            ) : null}

            <form onSubmit={onUpload} noValidate>
              <div className="grid gap-5 md:grid-cols-2">
                <label className="block md:col-span-2">
                  <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-brand-body-medium">
                    File
                  </span>
                  <input
                    id="admin-doc-file"
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                    onChange={onFileSelected}
                    disabled={uploading}
                    className="mt-2 block w-full text-sm text-brand-body-dark file:mr-4 file:rounded-md file:border-0 file:bg-brand-yellow file:px-4 file:py-2 file:text-sm file:font-semibold file:text-brand-charcoal hover:file:bg-brand-yellow-deep"
                  />
                </label>

                <label className="block">
                  <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-brand-body-medium">
                    Title
                  </span>
                  <input
                    type="text"
                    required
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    disabled={uploading}
                    className="mt-2 block h-11 w-full rounded-md border border-brand-surface bg-brand-white px-3 text-base text-brand-charcoal focus:border-brand-yellow focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                    placeholder="e.g., Annual Meeting Minutes, April 2026"
                  />
                </label>

                <label className="block">
                  <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-brand-body-medium">
                    Category
                  </span>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    disabled={uploading}
                    className="mt-2 block h-11 w-full rounded-md border border-brand-surface bg-brand-white px-3 text-base text-brand-charcoal focus:border-brand-yellow focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex items-center gap-3 md:col-span-2">
                  <input
                    type="checkbox"
                    checked={isOwnerOnly && !isAdminOnly}
                    onChange={(e) => setIsOwnerOnly(e.target.checked)}
                    disabled={uploading || isAdminOnly}
                    className="h-5 w-5 rounded border-brand-surface text-brand-yellow focus:ring-brand-yellow disabled:opacity-40"
                  />
                  <span
                    className={`text-sm ${isAdminOnly ? 'text-brand-body-medium' : 'text-brand-body-dark'}`}
                  >
                    Owner-only, requires sign-in to view (directory, etc.)
                  </span>
                </label>

                <label className="flex items-center gap-3 md:col-span-2">
                  <input
                    type="checkbox"
                    checked={isAdminOnly}
                    onChange={(e) => setIsAdminOnly(e.target.checked)}
                    disabled={uploading}
                    className="h-5 w-5 rounded border-brand-surface text-brand-yellow focus:ring-brand-yellow"
                  />
                  <span className="text-sm text-brand-body-dark">
                    Admin-only, admin and super-admin only (insurance, monthly financials, bids,
                    contracts)
                  </span>
                </label>

                {category === 'minutes' ? (
                  <label className="flex items-center gap-3 rounded-md border border-brand-surface bg-brand-surface/30 p-3 md:col-span-2">
                    <input
                      type="checkbox"
                      checked={isApproved}
                      onChange={(e) => setIsApproved(e.target.checked)}
                      disabled={uploading}
                      className="h-5 w-5 rounded border-brand-surface text-brand-yellow focus:ring-brand-yellow"
                    />
                    <span className="text-sm text-brand-body-dark">
                      Approved by the board (leave unchecked while minutes are still in draft; flip
                      this on the next upload or via the list below once the board approves them).
                    </span>
                  </label>
                ) : null}
              </div>

              <button
                type="submit"
                disabled={uploading}
                className="mt-6 inline-flex h-11 items-center justify-center gap-2 rounded-md bg-brand-yellow px-6 text-sm font-semibold text-brand-charcoal transition-colors hover:bg-brand-yellow-deep disabled:cursor-not-allowed disabled:opacity-60"
              >
                {uploading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={16} aria-hidden="true" />
                    Upload document
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Existing documents list */}
          <div>
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-xl font-bold text-brand-charcoal">All documents</h2>
              <p className="text-sm text-brand-body-medium">{docs?.length ?? '-'} total</p>
            </div>

            {loadError ? (
              <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                {loadError}
              </div>
            ) : docs === null ? (
              <p className="text-sm text-brand-body-medium">Loading...</p>
            ) : docs.length === 0 ? (
              <p className="text-sm text-brand-body-medium">No documents yet. Upload one above.</p>
            ) : (
              <ul className="divide-y divide-brand-surface rounded-lg border border-brand-surface bg-brand-white shadow-card">
                {docs.map((doc) => {
                  const statusInfo = STATUS_BADGE[doc.status];
                  const isMinutes = doc.category === 'minutes';
                  const isApproving = approvingId === doc.id;
                  return (
                    <li
                      key={doc.id}
                      className="flex flex-col gap-3 p-5 md:flex-row md:items-center md:gap-6"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-base font-bold text-brand-charcoal">
                          {formatDocTitle(doc)}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                          <span className="rounded bg-brand-surface px-2 py-0.5 text-brand-body-dark">
                            {categoryLabel(doc.category)}
                          </span>
                          {doc.is_admin_only ? (
                            <span className="rounded bg-red-100 px-2 py-0.5 font-semibold text-red-800">
                              Admin-only
                            </span>
                          ) : doc.is_owner_only ? (
                            <span className="rounded bg-brand-charcoal px-2 py-0.5 text-brand-yellow">
                              Owner-only
                            </span>
                          ) : (
                            <span className="rounded bg-green-50 px-2 py-0.5 text-green-800">
                              Public
                            </span>
                          )}
                          {isMinutes ? (
                            doc.is_approved ? (
                              <span className="rounded bg-green-100 px-2 py-0.5 font-semibold text-green-800">
                                Approved
                              </span>
                            ) : (
                              <span className="rounded bg-yellow-100 px-2 py-0.5 font-semibold text-yellow-800">
                                Unapproved
                              </span>
                            )
                          ) : null}
                          {statusInfo ? (
                            <span className={`rounded px-2 py-0.5 ${statusInfo.classes}`}>
                              {statusInfo.label}
                            </span>
                          ) : null}
                          <span className="text-brand-body-medium">{formatDate(doc.created)}</span>
                        </div>
                        <p className="mt-1 truncate text-xs text-brand-body-medium">
                          {doc.original_filename}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {isMinutes ? (
                          <button
                            type="button"
                            onClick={() => void onToggleApproval(doc)}
                            disabled={isApproving}
                            className={`inline-flex h-10 items-center gap-2 rounded-md px-3 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                              doc.is_approved
                                ? 'border border-brand-surface text-brand-body-dark hover:bg-brand-surface'
                                : 'bg-brand-yellow text-brand-charcoal hover:bg-brand-yellow-deep'
                            }`}
                            title={
                              doc.is_approved
                                ? 'Flip back to unapproved (audit-logged)'
                                : 'Approve these minutes (audit-logged)'
                            }
                          >
                            {doc.is_approved ? (
                              <Circle size={16} aria-hidden="true" />
                            ) : (
                              <CheckCircle2 size={16} aria-hidden="true" />
                            )}
                            {isApproving
                              ? 'Saving...'
                              : doc.is_approved
                                ? 'Mark unapproved'
                                : 'Approve'}
                          </button>
                        ) : null}
                        <a
                          href={buildFileUrl(doc, fileToken)}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={`${doc.title}.pdf`}
                          className="inline-flex h-10 items-center justify-center rounded-md border border-brand-surface px-4 text-sm font-semibold text-brand-charcoal transition-colors hover:bg-brand-surface"
                        >
                          Download
                        </a>
                        <button
                          type="button"
                          onClick={() => void onDelete(doc)}
                          className="inline-flex h-10 w-10 items-center justify-center rounded-md text-red-700 hover:bg-red-50"
                          aria-label={`Delete ${doc.title}`}
                        >
                          <Trash2 size={18} aria-hidden="true" />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </Container>
      </section>
    </>
  );
}
